import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button } from "@material-ui/core";
import { getGroupPlanContact, getGroupPlanContactTypes, submitGroupPlanContact } from "../../api/groupApi";
import CSPhoneInput from "../Common/CSPhoneInput";
import MessageDialog, { useMessageDialog } from "../Common/MessageDialog";

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Default shape of a single contact record.
 * Field names follow the NPA PDP YMS GroupPlanContactInfo Layout spreadsheet
 * and the project-wide PascalCase convention used in contactData.js.
 */
const emptyContact = {
  GroupNumber: "",
  ContactId: null,
  ContactTypeId: "",
  // ContactReason: "New",
  FirstName: "",
  LastName: "",
  ContactEffectiveDate: "",
  ContactExpirationDate: "",
  Address1: "",
  Address2: "",
  City: "",
  State: "",
  Zip: "",
  PhoneNumber: "",
  EmailAddress: "",
};

// ─── Component ────────────────────────────────────────────────────────────────

const GroupPlanContactInfo = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const groupNumber = searchParams.get("groupNumber") ?? "";

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      contacts: [{ ...emptyContact, GroupNumber: groupNumber, isNewContact: false }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "contacts" });

  // Watch the whole contacts array so conditional label/required marks re-render
  // when ContactType changes within any card.
  const watchedContacts = watch("contacts");

  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(!!groupNumber);
  const [contactTypes, setContactTypes] = useState([]);
  const { showMessage, dialogProps: errorDialogProps } = useMessageDialog();

  /** Convert an ISO date string (or null) to YYYY-MM-DD for <input type="date"> */
  const toDateInput = (iso) => (iso ? iso.substring(0, 10) : "");

  // ─── Load contact types, then existing contacts (chained) ─────────────────

  React.useEffect(() => {
    getGroupPlanContactTypes()
      .catch((err) => {
        console.warn("Failed to load contact types:", err.message);
        return [];
      })
      .then((types) => {
        setContactTypes(Array.isArray(types) ? types : []);

        if (!groupNumber) { setIsLoadingContacts(false); return; }
        

        return getGroupPlanContact(groupNumber)
          .then((data) => {
            if (Array.isArray(data) && data.length) {
              reset({
                contacts: data.map((c) => ({
                  GroupNumber:    c.groupNumber ?? groupNumber,
                    isNewContact:  false,
                  ContactId:      c.contactId ?? null,
                  ContactTypeId:  c.contactTypeId?.toUpperCase() ?? "",
                  // ContactReason:  c.contactReason  ?? "New",
                  FirstName:      c.firstName      ?? "",
                  LastName:       c.lastName       ?? "",
                  ContactEffectiveDate: toDateInput(c.contactEffectiveDate),
                  ContactExpirationDate: toDateInput(c.contactExpirationDate),
                  Address1:       c.address1       ?? "",
                  Address2:       c.address2       ?? "",
                  City:           c.city           ?? "",
                  State:          c.state          ?? "",
                  Zip:            c.zip            ?? "",
                  PhoneNumber:    c.phoneNumber ? `+1${c.phoneNumber}` : "",
                  EmailAddress:   c.emailAddress   ?? "",
                })),
              });
            }
          })
          .catch(async (err) => {
            await showMessage({
              message: `Unable to load existing contact information: ${err.message}`,
              title: "YourMedicareSolutions.com",
              messageType: "danger",
            });
          });
      })
      .finally(() => setIsLoadingContacts(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupNumber]);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /** Resolve a ContactType GUID to its display Text */
  const getContactTypeText = (guid) =>
    contactTypes.find((t) => (t.value ?? t.Value) === guid)?.text ?? "";

  const isHQ = (i) =>
    getContactTypeText(watchedContacts?.[i]?.ContactTypeId) === "Employer Group Plan Headquarters";

  const isPrimaryBilling = (i) =>
    getContactTypeText(watchedContacts?.[i]?.ContactTypeId) === "Primary Billing Contact";

  // ─── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    setIsProcessing(true);
    try {
      // API expects a flat Contact[] with camelCase field names
      const payload = {
        contacts: data.contacts.map((contact) => ({
          groupNumber:    contact.GroupNumber,
          contactId:      contact.ContactId ?? null,
          contactTypeId:  contact.ContactTypeId,
          // contactReason:  contact.ContactReason,
          firstName:      contact.FirstName,
          lastName:       contact.LastName,
          contactEffectiveDate: contact.ContactEffectiveDate || '01/01/0001',
          contactExpirationDate: contact.ContactExpirationDate || '12/31/9999',
          address1:       contact.Address1,
          address2:       contact.Address2,
          city:           contact.City,
          state:          contact.State,
          zip:            contact.Zip,
          phoneNumber:    contact.PhoneNumber?.replace(/^\+1/, "") ?? "",
          emailAddress:   contact.EmailAddress,
        })),
      };
      await submitGroupPlanContact(payload);
      setIsConfirmed(true);
    } catch (err) {
      await showMessage({
        message: `An error occurred while submitting your request: ${err.message}`,
        title: "YourMedicareSolutions.com",
        messageType: "danger",
      });
    }
    setIsProcessing(false);
  };

  // ─── Loading screen ────────────────────────────────────────────────────────

  if (isLoadingContacts) {
    return (
      <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
        <p>Loading…</p>
      </div>
    );
  }

  // ─── Confirmation screen ───────────────────────────────────────────────────

  if (isConfirmed) {
    return (
      <div
        role="main"
        className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
      >
        <div className="row d-md-flex">
          <section className="col-sm-12 internal-page">
            <div className="region region-content">

              {/* Breadcrumb */}
              <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                <div className="breadcrumb-wrap">
                  <div className="region--breadcrumb nst-1">
                    <section className="block-system block-system-breadcrumb-block">
                      <div role="navigation">
                        <ol className="breadcrumb">
                          <li>
                            <Link to="/">Home</Link>
                          </li>
                          <li>
                            <Link to="/GroupPlanManagement">Group Plan Management</Link>
                          </li>
                          <li><Link to={`/GroupDashboard?groupNumber=${groupNumber}`}>Group Dashboard</Link></li>
                          <li>Group plan contact information</li>
                        </ol>
                      </div>
                    </section>
                  </div>
                </div>
              </section>

              <h1 className="page-header">Group Plan Contact Information</h1>
              <div
                style={{
                  padding: "24px",
                  border: "1px solid #d4edda",
                  borderRadius: "6px",
                  background: "#f0f9f0",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "56px", color: "#16a34a", lineHeight: 1 }}>✓</div>
                <h2 style={{ color: "#0056a3" }}>Group Plan Contact Information Received</h2>
                <p>
                  Thank you. Your group contact information has been submitted
                  successfully.
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                  <Button
                    className="webform-button--next button js-form-submit form-submit btn-default btn"
                    onClick={() => window.location.reload()}
                  >
                    Submit Another Request
                  </Button>
                  <Button
                    className="webform-button--next button js-form-submit form-submit btn-default btn"
                    onClick={() => navigate(`/GroupDashboard?groupNumber=${groupNumber}`)}
                  >
                    Back to Group Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ─── Form ──────────────────────────────────────────────────────────────────

  return (
    <div
      role="main"
      className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
    >
      <div className="row d-md-flex">
        <section className="col-sm-12 internal-page">
          <div className="region region-content">

            {/* Breadcrumb */}
            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
              <div className="breadcrumb-wrap">
                <div className="region--breadcrumb nst-1">
                  <section className="block-system block-system-breadcrumb-block">
                    <div role="navigation">
                      <ol className="breadcrumb">
                        <li>
                          <Link to="/">Home</Link>
                        </li>
                        <li>
                          <Link to="/GroupPlanManagement">Group Plan Management</Link>
                        </li>
                        <li><Link to={`/GroupDashboard?groupNumber=${groupNumber}`}>Group Dashboard</Link></li>
                        <li>Group plan contact information</li>
                      </ol>
                    </div>
                  </section>
                </div>
              </div>
            </section>

            <h1 className="page-header">Group Plan Contact Information</h1>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>

              {fields.map((field, index) => {
                const hq = isHQ(index);
                const billing = isPrimaryBilling(index);

                return (
                  <div
                    key={field.id}
                    aria-label={`Contact ${index + 1}`}
                    className="panel panel-default"
                    style={{
                      marginTop: "64px",
                      padding: "16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      backgroundColor: "#fafbfd",
                    }}
                  >
                    {/* Remove button — hidden for the primary (first) contact */}
                    {field.isNewContact && (
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                        <Button
                          type="button"
                          className="webform-button--next button js-form-submit form-submit btn-default btn"
                          onClick={(e) => {
                            e.preventDefault();
                            remove(index);
                          }}
                          aria-label="Remove Contact"
                          style={{ background: "#b00020", color: "#fff" }}
                        >
                          Remove Contact
                        </Button>
                      </div>
                    )}

                    {/* <h3 style={{ marginTop: 0, color: "#0056a3" }}>
                      Contact {index + 1}
                    </h3> */}

                    {/* ── Row 1: Group Number · Contact Type ───────────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.GroupNumber`}
                          className="control-label"
                        >
                          Group Number
                        </label>
                        <input
                          id={`contacts.${index}.GroupNumber`}
                          type="text"
                          className="form-text form-control"
                          disabled
                          readOnly
                          {...register(`contacts.${index}.GroupNumber`)}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.ContactTypeId`}
                          className="control-label js-form-required form-required"
                        >
                          Contact Type
                        </label>
                        <select
                          id={`contacts.${index}.ContactTypeId`}
                          className="form-select form-control"
                          {...register(`contacts.${index}.ContactTypeId`, {
                            required: "Contact Type is required.",
                            onChange: (e) => {
                              const text = getContactTypeText(e.target.value);
                              if (text !== "Employer Group Plan Headquarters") {
                                clearErrors([
                                  `contacts.${index}.Address1`,
                                  `contacts.${index}.City`,
                                  `contacts.${index}.State`,
                                  `contacts.${index}.Zip`,
                                ]);
                              }
                              if (text !== "Primary Billing Contact") {
                                clearErrors(`contacts.${index}.EmailAddress`);
                              }
                            },
                          })}
                        >
                          {contactTypes.map((t) => {
                            const value = t.value ?? t.Value;
                            const text  = t.text  ?? t.Text;
                            return (
                              <option key={value} value={value}>
                                {text}
                              </option>
                            );
                          })}
                        </select>
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.ContactTypeId`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Row 2: Contact Reason ─────────────────────────────── */}
                    {/* <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.ContactReason`}
                          className="control-label js-form-required form-required"
                        >
                          Contact Request Reason
                        </label>
                        <select
                          id={`contacts.${index}.ContactReason`}
                          className="form-select form-control"
                          {...register(`contacts.${index}.ContactReason`, {
                            required: "Contact Request Reason is required.",
                          })}
                        >
                          <option value="New">New</option>
                          <option value="Update">Update</option>
                        </select>
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.ContactReason`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-sm-6 col-md-6" />
                    </div> */}

                    {/* ── Row 3: First Name · Last Name ─────────────────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.FirstName`}
                          className={`control-label${field.isNewContact ? " js-form-required form-required" : ""}`}
                        >
                          First Name
                        </label>
                        <input
                          id={`contacts.${index}.FirstName`}
                          type="text"
                          className={`form-text${field.isNewContact ? " required" : ""} form-control`}
                          {...register(`contacts.${index}.FirstName`, {
                            required: field.isNewContact ? "First Name is required." : false,
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.FirstName`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.LastName`}
                          className={`control-label${field.isNewContact ? " js-form-required form-required" : ""}`}
                        >
                          Last Name
                        </label>
                        <input
                          id={`contacts.${index}.LastName`}
                          type="text"
                          className={`form-text${field.isNewContact ? " required" : ""} form-control`}
                          {...register(`contacts.${index}.LastName`, {
                            required: field.isNewContact ? "Last Name is required." : false,
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.LastName`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Row 4: Effective Date · Expiration Date ───────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.ContactEffectiveDate`}
                          className="control-label js-form-required form-required"
                        >
                          Contact Effective Date
                        </label>
                        <input
                          id={`contacts.${index}.ContactEffectiveDate`}
                          type="date"
                          className="form-text required form-control"
                          {...register(`contacts.${index}.ContactEffectiveDate`, {
                            required: "Contact Effective Date is required.",
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.ContactEffectiveDate`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.ContactExpirationDate`}
                          className="control-label"
                        >
                          Contact Expiration Date
                        </label>
                        <input
                          id={`contacts.${index}.ContactExpirationDate`}
                          type="date"
                          className="form-text form-control"
                          {...register(`contacts.${index}.ContactExpirationDate`, {
                            validate: (val) => {
                              if (!val) return true;
                              const eff = getValues(
                                `contacts.${index}.ContactEffectiveDate`
                              );
                              if (eff && new Date(val) < new Date(eff)) {
                                return "Expiration Date cannot be earlier than Effective Date.";
                              }
                              return true;
                            },
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.ContactExpirationDate`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Row 5: Address · Address 2 ───────────────────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.Address1`}
                          className={`control-label${hq ? " js-form-required form-required" : ""}`}
                        >
                          Address
                        </label>
                        <input
                          id={`contacts.${index}.Address1`}
                          type="text"
                          className="form-text form-control"
                          {...register(`contacts.${index}.Address1`, {
                            validate: (val) => {
                              const ct = getContactTypeText(getValues(`contacts.${index}.ContactTypeId`));
                              if (
                                ct === "Employer Group Plan Headquarters" &&
                                !val?.trim()
                              ) {
                                return "Address is required for Employer Group Plan Headquarters.";
                              }
                              return true;
                            },
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.Address1`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.Address2`}
                          className="control-label"
                        >
                          Address 2
                        </label>
                        <input
                          id={`contacts.${index}.Address2`}
                          type="text"
                          className="form-text form-control"
                          {...register(`contacts.${index}.Address2`)}
                        />
                      </div>
                    </div>

                    {/* ── Row 6: City · State ───────────────────────────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.City`}
                          className={`control-label${hq ? " js-form-required form-required" : ""}`}
                        >
                          City
                        </label>
                        <input
                          id={`contacts.${index}.City`}
                          type="text"
                          className="form-text form-control"
                          {...register(`contacts.${index}.City`, {
                            validate: (val) => {
                              const ct = getContactTypeText(getValues(`contacts.${index}.ContactTypeId`));
                              if (
                                ct === "Employer Group Plan Headquarters" &&
                                !val?.trim()
                              ) {
                                return "City is required for Employer Group Plan Headquarters.";
                              }
                              return true;
                            },
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.City`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.State`}
                          className={`control-label${hq ? " js-form-required form-required" : ""}`}
                        >
                          State
                        </label>
                        <input
                          id={`contacts.${index}.State`}
                          type="text"
                          maxLength={2}
                          className="form-text form-control"
                          {...register(`contacts.${index}.State`, {
                            maxLength: {
                              value: 2,
                              message:
                                "Please enter a 2-letter state abbreviation.",
                            },
                            validate: (val) => {
                              const ct = getContactTypeText(getValues(`contacts.${index}.ContactTypeId`));
                              if (
                                ct === "Employer Group Plan Headquarters" &&
                                !val?.trim()
                              ) {
                                return "State is required for Employer Group Plan Headquarters.";
                              }
                              return true;
                            },
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.State`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Row 7: Zip · Phone Number ─────────────────────────── */}
                    <div className="row form-group">
                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.Zip`}
                          className={`control-label${hq ? " js-form-required form-required" : ""}`}
                        >
                          Zip
                        </label>
                        <input
                          id={`contacts.${index}.Zip`}
                          type="text"
                          className="form-text form-control"
                          {...register(`contacts.${index}.Zip`, {
                            validate: (val) => {
                              const ct = getContactTypeText(getValues(`contacts.${index}.ContactTypeId`));
                              if (
                                ct === "Employer Group Plan Headquarters" &&
                                !val?.trim()
                              ) {
                                return "Zip is required for Employer Group Plan Headquarters.";
                              }
                              return true;
                            },
                          })}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.Zip`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>

                      <div className="col-sm-6 col-md-6 form-item form-group">
                        <label
                          htmlFor={`contacts.${index}.PhoneNumber`}
                          className={`control-label${field.isNewContact ? " js-form-required form-required" : ""}`}
                        >
                          Phone Number
                        </label>
                        <CSPhoneInput
                          name={`contacts.${index}.PhoneNumber`}
                          id={`contacts.${index}.PhoneNumber`}
                          value={watchedContacts[index]?.PhoneNumber ?? ""}
                          onChange={() => { }}
                          control={control}
                          rules={{
                            required: field.isNewContact ? "Phone Number is required." : false,
                          }}
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`contacts.${index}.PhoneNumber`}
                          render={({ message }) => (
                            <div role="alert" aria-live="polite" className="error">
                              {message}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* ── Email (full width, conditional) ───────────────────── */}
                    <div className="form-item form-group">
                      <label
                        htmlFor={`contacts.${index}.EmailAddress`}
                        className={`control-label${billing ? " js-form-required form-required" : ""}`}
                      >
                        Email
                      </label>
                      <input
                        id={`contacts.${index}.EmailAddress`}
                        type="email"
                        className="form-text form-control"
                        {...register(`contacts.${index}.EmailAddress`, {
                          validate: (val) => {
                            const ct = getContactTypeText(getValues(`contacts.${index}.ContactTypeId`));
                            if (
                              ct === "Primary Billing Contact" &&
                              !val?.trim()
                            ) {
                              return "Email is required for Primary Billing Contact.";
                            }
                            if (
                              val?.trim() &&
                              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
                            ) {
                              return "Please enter a valid email address.";
                            }
                            return true;
                          },
                        })}
                      />
                      <ErrorMessage
                        errors={errors}
                        name={`contacts.${index}.EmailAddress`}
                        render={({ message }) => (
                          <div role="alert" aria-live="polite" className="error">
                            {message}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                );
              })}

              {/* ── Add Another Contact ─────────────────────────────────────── */}
              <div style={{ marginTop: "24px" }}>
                <Button
                  type="button"
                  className="webform-button--next button js-form-submit form-submit btn-default btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    append(
                      { ...emptyContact, GroupNumber: groupNumber, isNewContact: true },
                      { shouldFocus: false }
                    );
                  }}
                  style={{ background: "#0056a3", color: "#fff", border: "1px solid #0056a3" }}
                >
                  Add Contact
                </Button>
              </div>

              {/* ── Submit error dialog ─────────────────────────────────────── */}
              <MessageDialog {...errorDialogProps} />

              {/* ── Submit ──────────────────────────────────────────────────── */}
              <Button
                type="submit"
                className="webform-button--next button js-form-submit form-submit btn-default btn"
                disabled={isProcessing}
                style={{ marginTop: "24px" }}
              >
                {isProcessing ? "Submitting…" : "Submit Contact Information"}
              </Button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GroupPlanContactInfo;
